import { priorityPartners, partners, realEstatePartners } from "@/mocks/homeData";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

interface Partner {
  name: string;
  logo?: string;
  website?: string;
  darkCard?: boolean;
  darkLogo?: boolean;
  boost?: boolean;
  boostXl?: boolean;
  description?: string;
  location?: string;
  founded?: string;
  products?: string[];
}

export default function PartnersPage() {
  const { t } = useTranslation();

  const allPartnerLists = [
    {
      title: t("partners.keyPartners"),
      items: priorityPartners as Partner[],
    },
    {
      title: t("partners.allPartners"),
      items: partners as Partner[],
    },
    {
      title: t("partners.realEstate"),
      items: realEstatePartners as Partner[],
    },
  ];

  const renderCard = (partner: Partner, index: number, sectionIndex: number) => {
    const isDarkCard = partner.darkCard;
    const isBoost = partner.boost;
    const isBoostXl = partner.boostXl;

    const cardClasses = isDarkCard
      ? "bg-background-900 border-background-700 text-background-50"
      : "bg-background-200 border-background-300/60 text-foreground-800";

    const hoverColor = isDarkCard
      ? "group-hover:text-primary-400"
      : "group-hover:text-primary-600";

    return (
      <div
        key={index}
        className={`rounded-2xl border overflow-hidden group transition-all duration-300 flex flex-col ${cardClasses} ${
          partner.website ? "hover:border-primary-500/60 cursor-pointer" : ""
        } ${sectionIndex === 0 ? "w-40 sm:w-44 lg:w-48 flex-none" : ""}`}
      >
        {partner.website ? (
          <a
            href={partner.website}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 w-full flex flex-col items-center"
          >
            <div className={`flex-1 w-full flex items-center justify-center min-h-[100px] md:min-h-[120px] ${
              isBoostXl ? "p-2 md:p-3" : "p-4 md:p-5"
            }`}>
              {partner.logo ? (
                <img
                  src={partner.logo}
                  alt={partner.name}
                  className={`w-auto max-w-full object-contain transition-transform duration-400 group-hover:scale-105 ${
                    isBoostXl ? "h-24 md:h-28" : isBoost ? "h-16 md:h-20" : "h-11 md:h-14"
                  }`}
                />
              ) : (
                <div className="w-12 h-12 rounded-xl bg-secondary-500/10 flex items-center justify-center">
                  <i className="ri-building-2-line text-secondary-500 text-2xl"></i>
                </div>
              )}
            </div>
            <div className="w-full px-3 pb-3 pt-1 text-center">
              <span className={`font-heading text-[11px] md:text-xs font-semibold ${hoverColor} transition-colors leading-tight block`}>
                {partner.name}
              </span>
            </div>
          </a>
        ) : (
          <>
            <div className={`flex-1 w-full flex items-center justify-center min-h-[100px] md:min-h-[120px] ${
              isBoostXl ? "p-2 md:p-3" : "p-4 md:p-5"
            }`}>
              {partner.logo ? (
                <img
                  src={partner.logo}
                  alt={partner.name}
                  className={`w-auto max-w-full object-contain ${
                    isBoostXl ? "h-24 md:h-28" : isBoost ? "h-16 md:h-20" : "h-11 md:h-14"
                  }`}
                />
              ) : (
                <div className="w-12 h-12 rounded-xl bg-secondary-500/10 flex items-center justify-center">
                  <i className="ri-building-2-line text-secondary-500 text-2xl"></i>
                </div>
              )}
            </div>
            <div className="w-full px-3 pb-3 pt-1 text-center">
              <span className="font-heading text-[11px] md:text-xs font-semibold leading-tight block">
                {partner.name}
              </span>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background-950">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-28 md:pt-36 pb-12 md:pb-16 px-4 md:px-6 max-w-6xl mx-auto">
        <div className="text-center">
          <span className="inline-block px-4 py-1.5 border border-secondary-400/30 text-secondary-300 text-xs md:text-sm font-medium rounded-full whitespace-nowrap mb-4">
            {t("partners.badge")}
          </span>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 tracking-tight">
            {t("partners.pageHeading")}
          </h1>
          <p className="text-sm md:text-base text-background-50/60 mt-4 max-w-xl mx-auto leading-relaxed">
            {t("partners.pageDescription")}
          </p>
        </div>
      </section>

      {/* Partner Sections */}
      <section className="px-4 md:px-6 max-w-6xl mx-auto pb-16 md:pb-24">
        {allPartnerLists.map((list, sectionIndex) => (
          <div key={sectionIndex} className={sectionIndex > 0 ? "mt-12 md:mt-16" : ""}>
            <div className="flex items-center gap-4 mb-6">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-primary-400/40 to-transparent"></div>
              <span className="text-xs md:text-sm text-background-50/60 font-medium tracking-wide uppercase whitespace-nowrap">
                {list.title}
              </span>
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-primary-400/40 to-transparent"></div>
            </div>

            {sectionIndex === 0 ? (
              <div className="w-fit max-w-full mx-auto flex flex-wrap justify-center gap-4 md:gap-5">
                {list.items.map((partner, index) => renderCard(partner, index, sectionIndex))}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
                {list.items.map((partner, index) => renderCard(partner, index, sectionIndex))}
              </div>
            )}

            {/* Description section for Priority Partners */}
            {sectionIndex === 0 && (
              <div className="mt-8 md:mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {priorityPartners.filter((p: Partner) => p.description).map((partner: Partner, index: number) => (
                  <div key={index} className="rounded-2xl border border-background-700 bg-background-900/60 overflow-hidden hover:border-primary-400/30 transition-all duration-300">
                    {/* Logo area: centered, with light bg so dark logos pop */}
                    <div className="w-full flex items-center justify-center px-5 pt-7 pb-5 bg-background-100/50">
                      {partner.logo ? (
                        <img
                          src={partner.logo}
                          alt={partner.name}
                          className={`w-auto max-w-full object-contain ${
                            partner.name === "Dormakaba AG" ? "h-28 md:h-40" : "h-20 md:h-28"
                          }`}
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-xl bg-secondary-500/10 flex items-center justify-center">
                          <i className="ri-building-2-line text-secondary-500 text-2xl"></i>
                        </div>
                      )}
                    </div>
                    {/* Name below logo */}
                    <div className="px-5 pt-3 pb-0">
                      <h3 className="font-heading text-sm md:text-base font-bold text-background-50 leading-snug">
                        {partner.name}
                      </h3>
                    </div>
                    {/* Body content */}
                    <div className="px-5 pt-2 pb-5">
                      <p className="text-xs md:text-sm text-background-50/60 leading-relaxed mb-3">
                        {partner.description}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {partner.products?.map((product: string, pIndex: number) => (
                          <span key={pIndex} className="px-2.5 py-1 rounded-full text-[10px] md:text-xs font-medium bg-primary-500/15 text-primary-400 whitespace-nowrap">
                            {product}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-background-700/50">
                        {partner.location && (
                          <span className="text-[11px] text-background-50/40 flex items-center gap-1">
                            <i className="ri-map-pin-line text-xs"></i>
                            {partner.location}
                          </span>
                        )}
                        {partner.founded && (
                          <span className="text-[11px] text-background-50/40 flex items-center gap-1">
                            <i className="ri-calendar-line text-xs"></i>
                            {partner.founded}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </section>

      <Footer />
    </div>
  );
}